import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '../../../src/admin/types';
import { useAuth } from '../auth/AuthContext';
import { POST_LOGIN_REDIRECT_KEY } from '../auth/postLoginRedirect';

const ROLE_RANK: Record<Role, number> = { viewer: 1, editor: 2, admin: 3 };

interface ProtectedRouteProps {
  children: ReactNode;
  minRole?: Role;
}

/**
 * Route guard.
 * - `loading` → renders nothing (avoid flicker before AuthContext settles).
 * - `anonymous` → save current path to sessionStorage so AuthCallback can
 *   restore it post-login (in-memory React state would be lost across the
 *   Cognito OAuth round trip), then redirect to `/auth/login`.
 * - `authenticated` but missing or below `minRole` → render an inline
 *   "permission denied" message (no redirect).
 */
export function ProtectedRoute({
  children,
  minRole = 'viewer',
}: ProtectedRouteProps): JSX.Element | null {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'loading') return null;
  if (status === 'anonymous') {
    const path = `${location.pathname}${location.search}`;
    if (path !== '/' && path !== '/auth/login' && path !== '/auth/callback') {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
    }
    return <Navigate to="/auth/login" replace />;
  }
  const role = user?.role ?? null;
  if (!role || ROLE_RANK[role] < ROLE_RANK[minRole]) {
    return (
      <main className="p-6 text-sm text-gray-600">このページを表示する権限がありません。</main>
    );
  }
  return <>{children}</>;
}
